import zoomPlugin from "chartjs-plugin-zoom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart,
  ChartConfiguration,
  Legend,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  PieController,
  ArcElement,
  Tooltip,
  Filler,
} from "chart.js";

// Chart.js module registry
// Register Chart.js modules and plugin defaults globally to all charts
Chart.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  PieController,
  ArcElement,
  Tooltip,
  Filler
);

// Handler methods

// Not in use
const filterEveryOtherAxisTick = (value: any, index: number) => {
  if (index % 2 === 0) {
    return value;
  } else {
    return "";
  }
};

// Custom plugins

/**
 * Used to remove every second y-axis tick lines to avoid line amount excessivity
 */
const FilterEverySecondYAxisLines = {
  id: "filterEveryOtherAxisLines",
  beforeDraw(chart: any) {
    const yAxis = chart.scales.y;
    const ticks = yAxis.ticks;

    ticks.forEach((tick: any, index: number) => {
      const firstTick = index === 0;
      const evenTick = index % 2 === 0;

      if (evenTick || firstTick) {
        tick.markDelete = true;
      }
    });
    yAxis.ticks = ticks.filter((obj: any) => !obj.markDelete);
  },
};

// Chart configurations:

/**
 * Pie chart configuration for showing alerts handled by Emaac
 */
export const EMAAC_ALERTS_PIE_CHART_CONFIGS: ChartConfiguration<"pie"> = {
  type: "pie",
  data: {
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
        position: "nearest",
      },
    },
  },
};

/**
 * Alarm reason stacked horizontal bar chart
 */
export const ALERTS_BY_ALARM_REASON_CHART_CONFIGS: ChartConfiguration<"bar"> = {
  type: "bar",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        display: false,
        max: 0,
      },
      y: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        beginAtZero: true,
      },
    },
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgb(220, 221, 223)",
          },
          mode: "xy",
        },
      },
    },
  },
  plugins: [ChartDataLabels], // zoomPlugin ],
};

/**
 * Configuration for the Alerts per hour stacked bar chart
 */
export const ALERTS_PER_HOUR_CHART_CONFIGS: ChartConfiguration<"bar"> = {
  type: "bar",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
      },
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgb(220, 221, 223)",
          },
          mode: "xy",
        },
      },
    },
  },
  plugins: [Legend, FilterEverySecondYAxisLines], //, zoomPlugin],
};

/**
 * Configuration for the Alerts per weekday stacked bar chart
 */
export const ALERTS_PER_DAY_CHART_CONFIGS: ChartConfiguration<"bar"> = {
  type: "bar",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
      },

      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgb(220, 221, 223)",
          },
          mode: "xy",
        },
      },
    },
  },
  plugins: [Legend, FilterEverySecondYAxisLines], //, zoomPlugin],
};

/**
 * Configuration for the Average alert durations single horizontal bar chart
 */
export const AVERAGE_ALERT_DURATIONS_CHART_CONFIGS: ChartConfiguration<"bar"> =
  {
    type: "bar",
    data: {
      labels: [],
      datasets: [],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          min: 0,
          stacked: true,
          display: false,
        },
        y: {
          stacked: true,
          display: false,
        },
      },
      hover: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mode: null as any,
      },
      plugins: {
        tooltip: {
          enabled: false,
        },
      },
    },
  };

  /**
 * Configuration for the active users per weekday stacked bar chart
 */
  export const ACTIVE_USERS_PER_DAY_CHART_CONFIGS: ChartConfiguration<"bar"> = {
    type: "bar",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
      },

      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgb(220, 221, 223)",
          },
          mode: "xy",
        },
      },
    },
  },
  plugins: [Legend, FilterEverySecondYAxisLines], //, zoomPlugin],
  }

/**
 * Base configuration for the operator response-time distribution line chart (Alarm Center tab).
 * Single interpolated line of 1-second bins (zero bins filtered out), combined across device types.
 * Scale and legend options are applied as local overrides in ResponseTimeHistogramComponent.
 */
export const RESPONSE_TIME_HISTOGRAM_CHART_CONFIGS: ChartConfiguration<"line"> = {
  type: "line",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#707070",
          font: { size: 12 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          display: true,
          precision: 0,
          color: "#707070",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
  },
  plugins: [Legend],
};

/**
 * Configuration for the Active users per hour stacked bar chart
 */
export const ACTIVE_USERS_PER_HOUR_CHART_CONFIGS: ChartConfiguration<"bar"> = {
  type: "bar",
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          display: true,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
      },
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgb(220, 221, 223)",
          },
          mode: "xy",
        },
      },
    },
  },
  plugins: [Legend, FilterEverySecondYAxisLines], //, zoomPlugin],
};